import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { TranslatePipe } from '@ngx-translate/core';

interface DeveloperEndpoint {
  methods: string[];
  path: string;
  scope: string;
  titleKey: string;
  descKey: string;
}

// Mirrors the real, live endpoints of Portal.DevFunctions (docs/KONZEPT-Developer-Platform.md) —
// method/path/scope are technical and stay untranslated, only title/desc are i18n keys.
const ENDPOINTS: DeveloperEndpoint[] = [
  {
    methods: ['GET'], path: '/company', scope: 'Unternehmen:Read',
    titleKey: 'developer.endpoint.company.title', descKey: 'developer.endpoint.company.desc',
  },
  {
    methods: ['GET'], path: '/departments', scope: 'Abteilungen:Read',
    titleKey: 'developer.endpoint.departments.title', descKey: 'developer.endpoint.departments.desc',
  },
  {
    methods: ['GET'], path: '/employees', scope: 'Mitarbeiter:Read',
    titleKey: 'developer.endpoint.employees.title', descKey: 'developer.endpoint.employees.desc',
  },
  {
    methods: ['GET', 'POST', 'PATCH'], path: '/tablestack/reservations', scope: 'TableStack:Read/Write',
    titleKey: 'developer.endpoint.reservations.title', descKey: 'developer.endpoint.reservations.desc',
  },
  {
    methods: ['GET', 'PUT'], path: '/menu/items', scope: 'Speisekarte:Read/Write',
    titleKey: 'developer.endpoint.menu.title', descKey: 'developer.endpoint.menu.desc',
  },
  {
    methods: ['GET'], path: '/openapi.json', scope: 'öffentlich',
    titleKey: 'developer.endpoint.openapi.title', descKey: 'developer.endpoint.openapi.desc',
  },
];

@Component({
    selector: 'app-developer',
    templateUrl: './developer.page.html',
    styleUrls: ['./developer.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PortalNavbarComponent,
        IonicModule,
        PortalFooterComponent,
        TranslatePipe,
    ],
})
export class DeveloperPage implements OnInit, OnDestroy {
  readonly endpoints = ENDPOINTS;
  readonly developerPortalUrl = 'https://developer.gastro-management.ch';
  readonly apiBaseUrl = 'https://developer-api.gastro-management.ch/api/v1';

  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly seo: SeoService,
    private readonly portalAuth: PortalAuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'developer.seoTitle', descriptionKey: 'developer.heroSub', path: '/developer' });

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
